import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [emojiAnalytics, setEmojiAnalytics] = useState({ emojis: [], totals: {} });
  const [qrScanAnalytics, setQrScanAnalytics] = useState({ scans: [], totals: {} });
  const [connectionStatus, setConnectionStatus] = useState([]);
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    totalFeedback: 0,
    totalLocations: 0,
    activeSessions: 0,
    todayFeedback: 0,
    satisfactionRate: 0,
    responseRate: 0,
    avgResponseTime: 0
  });
  const [loading, setLoading] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    location: '',
    review_url: '',
    role: 'user',
    session_expiry_days: 7
  });
  const [editingUser, setEditingUser] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordChange, setPasswordChange] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  };

  // Helper function to handle authentication errors
  const handleAuthError = (response, error) => {
    if (response && (response.status === 401 || response.status === 403)) {
      console.log('Admin token expired or invalid, logging out...');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      onLogout();
      return true;
    }
    return false;
  };
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('today');
  const [filteredFeedback, setFilteredFeedback] = useState([]);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchDashboardStats();
      fetchConnectionStatus();
      fetchFeedback();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
      fetchEmojiAnalytics();
      fetchQrScanAnalytics();
    } else if (activeTab === 'feedback') {
      fetchFeedback();
    }
    fetchConnectionStatus();
  }, [activeTab]);

  // Smart polling for connection status
  useEffect(() => {
    let interval;
    
    const startPolling = () => {
      // Check if any locations are offline to determine polling frequency
      const hasOfflineLocations = Array.isArray(connectionStatus) ? connectionStatus.some(location => location.status === 'offline') : false;
      const pollInterval = hasOfflineLocations ? 10000 : 30000; // 10s if offline, 30s if all online
      
      interval = setInterval(() => {
        fetchConnectionStatus();
      }, pollInterval);
    };
    
    // Start initial polling
    startPolling();
    
    // Restart polling when connection status changes
    const restartInterval = () => {
      if (interval) clearInterval(interval);
      startPolling();
    };
    
    // Restart polling when connection status changes
    if (Array.isArray(connectionStatus) && connectionStatus.length > 0) {
      restartInterval();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [connectionStatus]);

  // Event-driven updates for connection status
  useEffect(() => {
    const handleDeviceLogin = () => {
      // Immediately refresh connection status when a device logs in
      triggerConnectionStatusRefresh();
    };

    const handleDeviceLogout = () => {
      // Immediately refresh connection status when a device logs out
      triggerConnectionStatusRefresh();
    };

    // Listen for device login/logout events
    window.addEventListener('deviceLogin', handleDeviceLogin);
    window.addEventListener('deviceLogout', handleDeviceLogout);

    return () => {
      window.removeEventListener('deviceLogin', handleDeviceLogin);
      window.removeEventListener('deviceLogout', handleDeviceLogout);
    };
  }, []);

  // Filter and sort feedback data
  useEffect(() => {
    let filtered = Array.isArray(recentFeedback) ? [...recentFeedback] : [];

    // Apply rating filter
    if (feedbackFilter !== 'all') {
      filtered = filtered.filter(feedback => feedback && feedback.rating === feedbackFilter);
    }

    // Apply action filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(feedback => feedback && feedback.action_type === actionFilter);
    }

    // Apply time-based filtering
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - thisWeek.getDay());
    const lastWeek = new Date(thisWeek);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    switch (sortBy) {
      case 'today':
        filtered = filtered.filter(feedback => new Date(feedback.created_at) >= today);
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'yesterday':
        filtered = filtered.filter(feedback => {
          const feedbackDate = new Date(feedback.created_at);
          return feedbackDate >= yesterday && feedbackDate < today;
        });
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'thisWeek':
        filtered = filtered.filter(feedback => new Date(feedback.created_at) >= thisWeek);
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'lastWeek':
        filtered = filtered.filter(feedback => {
          const feedbackDate = new Date(feedback.created_at);
          return feedbackDate >= lastWeek && feedbackDate < thisWeek;
        });
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'thisMonth':
        filtered = filtered.filter(feedback => new Date(feedback.created_at) >= thisMonth);
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'lastMonth':
        filtered = filtered.filter(feedback => {
          const feedbackDate = new Date(feedback.created_at);
          return feedbackDate >= lastMonth && feedbackDate < thisMonth;
        });
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'rating':
        const ratingOrder = {
          'very-satisfied': 5,
          'satisfied': 4,
          'neutral': 3,
          'dissatisfied': 2,
          'very-dissatisfied': 1
        };
        filtered.sort((a, b) => ratingOrder[b.rating] - ratingOrder[a.rating]);
        break;
      case 'location':
        filtered.sort((a, b) => (a.location || '').localeCompare(b.location || ''));
        break;
      case 'custom':
        // For custom, we'll show all data without time filtering
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      default:
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
    }

    setFilteredFeedback(filtered);
  }, [recentFeedback, feedbackFilter, actionFilter, sortBy]);

  const fetchDashboardStats = async () => {
    try {
      // Fetch users count
      const usersRes = await fetch('/api/admin/users', { headers: getAuthHeaders() });
      if (handleAuthError(usersRes)) return;
      const usersData = await usersRes.json();
      
      // Fetch analytics for feedback count
      const analyticsRes = await fetch('/api/admin/analytics', { headers: getAuthHeaders() });
      if (handleAuthError(analyticsRes)) return;
      const analyticsData = await analyticsRes.json();
      
      // Fetch recent feedback for today's count and satisfaction rate
      const feedbackRes = await fetch('/api/admin/feedback?limit=1000', { headers: getAuthHeaders() });
      if (handleAuthError(feedbackRes)) return;
      const feedbackData = await feedbackRes.json();
      
      // Check if data is valid arrays, otherwise use empty arrays
      const safeAnalyticsData = Array.isArray(analyticsData) ? analyticsData : [];
      const safeFeedbackData = Array.isArray(feedbackData) ? feedbackData : [];
      const safeUsersData = Array.isArray(usersData) ? usersData : [];
      
      const totalFeedback = safeAnalyticsData.reduce((sum, location) => sum + (location.total || 0), 0);
      const totalLocations = safeAnalyticsData.length;
      
      // Calculate today's feedback
      const today = new Date().toDateString();
      const todayFeedback = safeFeedbackData.filter(f => 
        f.created_at && new Date(f.created_at).toDateString() === today
      ).length;
      
      // Calculate satisfaction rate (very-satisfied + satisfied)
      const satisfiedCount = safeFeedbackData.filter(f => 
        f.rating === 'very-satisfied' || f.rating === 'satisfied'
      ).length;
      const satisfactionRate = totalFeedback > 0 ? Math.round((satisfiedCount / totalFeedback) * 100) : 0;
      
      // Calculate response rate (submitted vs total)
      const submittedCount = safeFeedbackData.filter(f => f.action_type === 'submitted').length;
      const responseRate = totalFeedback > 0 ? Math.round((submittedCount / totalFeedback) * 100) : 0;
      
      // Calculate average response time (mock for now)
      const avgResponseTime = 2.5; // hours
      
      setDashboardStats({
        totalUsers: safeUsersData.length,
        totalFeedback,
        totalLocations,
        activeSessions: Array.isArray(connectionStatus) ? connectionStatus.filter(c => c.status === 'online').length : 0,
        todayFeedback,
        satisfactionRate,
        responseRate,
        avgResponseTime
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/users', { headers: getAuthHeaders() });
      if (handleAuthError(response)) return;
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/feedback?limit=100', { headers: getAuthHeaders() });
      if (handleAuthError(response)) return;
      const data = await response.json();
      setRecentFeedback(data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/analytics', { headers: getAuthHeaders() });
      if (handleAuthError(response)) return;
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmojiAnalytics = async () => {
    try {
      const response = await fetch('/api/admin/analytics/emoji', { headers: getAuthHeaders() });
      if (handleAuthError(response)) return;
      const data = await response.json();
      setEmojiAnalytics(data);
    } catch (error) {
      console.error('Error fetching emoji analytics:', error);
    }
  };

  const fetchQrScanAnalytics = async () => {
    try {
      const response = await fetch('/api/admin/analytics/qr-scans', { headers: getAuthHeaders() });
      if (handleAuthError(response)) return;
      const data = await response.json();
      setQrScanAnalytics(data);
    } catch (error) {
      console.error('Error fetching QR scan analytics:', error);
    }
  };

  const fetchConnectionStatus = async () => {
    try {
      const response = await fetch('/api/admin/connection-status', { headers: getAuthHeaders() });
      if (handleAuthError(response)) return;
      const data = await response.json();
      setConnectionStatus(data);
    } catch (error) {
      console.error('Error fetching connection status:', error);
    }
  };

  // Trigger immediate connection status refresh (for event-driven updates)
  const triggerConnectionStatusRefresh = async () => {
    try {
      const response = await fetch('/api/admin/connection-status/refresh', { 
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (handleAuthError(response)) return;
      await fetchConnectionStatus(); // Immediately fetch updated status
    } catch (error) {
      console.error('Error triggering connection status refresh:', error);
    }
  };

  const getEmojiForRating = (rating) => {
    switch (rating) {
      case 'very-satisfied': return '😊';
      case 'satisfied': return '😊';
      case 'neutral': return '🤔';
      case 'dissatisfied': return '😢';
      case 'very-dissatisfied': return '😠';
      default: return '❓';
    }
  };

  const getRatingLabel = (rating) => {
    switch (rating) {
      case 'very-satisfied': return 'Very Satisfied';
      case 'satisfied': return 'Very Satisfied';
      case 'neutral': return 'Not Sure';
      case 'dissatisfied': return 'Sad';
      case 'very-dissatisfied': return 'Angry';
      default: return rating;
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone; // Return original if not 10 digits
  };

  const getFilteredSummaryStats = () => {
    const safeFilteredFeedback = Array.isArray(filteredFeedback) ? filteredFeedback : [];
    const total = safeFilteredFeedback.length;
    const withPhone = safeFilteredFeedback.filter(f => f.action_type === 'submitted' && (f.rating === 'dissatisfied' || f.rating === 'very-dissatisfied')).length;
    const positive = safeFilteredFeedback.filter(f => f.rating === 'very-satisfied' || f.rating === 'satisfied').length;
    const negative = safeFilteredFeedback.filter(f => f.rating === 'dissatisfied' || f.rating === 'very-dissatisfied').length;
    
    return { total, withPhone, positive, negative };
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.username || !newUser.password || !newUser.location) {
      alert('Please fill in username, password, and location name');
      return;
    }

    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newUser),
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        setNewUser({ username: '', password: '', location: '', review_url: '', role: 'user', session_expiry_days: 7 });
        setShowCreateUser(false);
        fetchUsers();
        alert('User created successfully!');
      } else {
        const error = await response.json();
        if (error.errors && error.errors.length > 0) {
          alert(`Error: ${error.errors[0].msg}`);
        } else if (error.error) {
          alert(`Error: ${error.error}`);
        } else {
          alert('Error: Unknown error occurred');
        }
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      ...user,
      password: '' // Don't prefill password for security
    });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser.username || !editingUser.location) {
      alert('Please fill in username and location name');
      return;
    }

    try {
      const response = await fetch(`/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editingUser),
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        setEditingUser(null);
        fetchUsers();
        alert('User updated successfully!');
      } else {
        const error = await response.json();
        if (error.errors && error.errors.length > 0) {
          alert(`Error: ${error.errors[0].msg}`);
        } else if (error.error) {
          alert(`Error: ${error.error}`);
        } else {
          alert('Error: Unknown error occurred');
        }
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordChange.currentPassword || !passwordChange.newPassword) {
      alert('Please fill in current and new passwords');
      return;
    }
    if (passwordChange.newPassword !== passwordChange.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    try {
      const response = await fetch('/api/admin/change-password', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          currentPassword: passwordChange.currentPassword,
          newPassword: passwordChange.newPassword,
          adminId: 1 // Assuming admin ID is 1, should be dynamic in real app
        }),
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        setPasswordChange({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowChangePassword(false);
        alert('Password changed successfully!');
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchUsers();
        alert('User deleted successfully!');
      } else {
        alert('Error deleting user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user');
    }
  };

  const handleLogoutUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to logout ${username} from all devices?`)) return;

    try {
      const response = await fetch(`/api/admin/users/${userId}/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        const result = await response.json();
        alert(result.message || 'User logged out successfully!');
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error logging out user:', error);
      alert('Error logging out user');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const tabVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <motion.div 
      className="admin-dashboard"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="admin-header">
        <h1>🏢 Admin Dashboard</h1>
        <div className="admin-actions">
          <button 
            onClick={() => setShowChangePassword(true)} 
            className="change-password-btn"
          >
            🔐 Change Password
          </button>
          <button onClick={onLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users & Locations
        </button>
        <button 
          className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📈 Analytics
        </button>
        <button 
          className={`tab-btn ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          📝 Recent Feedback
        </button>
      </div>

      <div className="admin-content">
        {/* Overview Dashboard */}
        {activeTab === 'overview' && (
          <motion.div 
            className="overview-dashboard"
            variants={tabVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Key Metrics Row */}
            <div className="metrics-row">
              <div className="metric-card primary">
                <div className="metric-icon">📊</div>
                <div className="metric-content">
                  <div className="metric-number">{dashboardStats.totalFeedback}</div>
                  <div className="metric-label">Total Feedback</div>
                  <div className="metric-change positive">+{dashboardStats.todayFeedback} today</div>
            </div>
          </div>
              <div className="metric-card success">
                <div className="metric-icon">😊</div>
                <div className="metric-content">
                  <div className="metric-number">{dashboardStats.satisfactionRate}%</div>
                  <div className="metric-label">Satisfaction Rate</div>
                  <div className="metric-change positive">Above target</div>
            </div>
          </div>
              <div className="metric-card warning">
                <div className="metric-icon">📍</div>
                <div className="metric-content">
                  <div className="metric-number">{dashboardStats.totalLocations}</div>
                  <div className="metric-label">Active Locations</div>
                  <div className="metric-change neutral">{dashboardStats.activeSessions} online</div>
            </div>
          </div>
              <div className="metric-card info">
                <div className="metric-icon">⏱️</div>
                <div className="metric-content">
                  <div className="metric-number">{dashboardStats.avgResponseTime}h</div>
                  <div className="metric-label">Avg Response Time</div>
                  <div className="metric-change positive">Fast response</div>
            </div>
          </div>
        </div>

            {/* Action Items and Alerts */}
            <div className="dashboard-row">
              <div className="action-items-card">
                <div className="card-header">
                  <h3>🚨 Action Items</h3>
                  <span className="badge urgent">2</span>
                </div>
                <div className="action-list">
                  {Array.isArray(connectionStatus) && connectionStatus.filter(location => location.status === 'offline').length > 0 && (
                    <div className="action-item urgent">
                      <div className="action-icon">⚠️</div>
                      <div className="action-content">
                        <div className="action-title">Offline Locations</div>
                        <div className="action-description">
                          {Array.isArray(connectionStatus) ? connectionStatus.filter(location => location.status === 'offline').length : 0} location(s) are offline
                        </div>
            </div>
          </div>
        )}
                  {Array.isArray(recentFeedback) && recentFeedback.filter(f => f.action_type === 'submitted' && (f.rating === 'dissatisfied' || f.rating === 'very-dissatisfied')).length > 0 && (
                    <div className="action-item high">
                      <div className="action-icon">😠</div>
                      <div className="action-content">
                        <div className="action-title">Negative Feedback</div>
                        <div className="action-description">
                          {Array.isArray(recentFeedback) ? recentFeedback.filter(f => f.action_type === 'submitted' && (f.rating === 'dissatisfied' || f.rating === 'very-dissatisfied')).length : 0} customer(s) need follow-up
                        </div>
                      </div>
                    </div>
                  )}
                  {Array.isArray(recentFeedback) && recentFeedback.filter(f => f.action_type === 'submitted' && f.rating === 'very-satisfied').length > 0 && (
                    <div className="action-item low">
                      <div className="action-icon">😊</div>
                      <div className="action-content">
                        <div className="action-title">Positive Reviews</div>
                        <div className="action-description">
                          {Array.isArray(recentFeedback) ? recentFeedback.filter(f => f.action_type === 'submitted' && f.rating === 'very-satisfied').length : 0} customer(s) left positive feedback
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="quick-stats-card">
          <div className="card-header">
                  <h3>📈 Quick Stats</h3>
              </div>
                <div className="quick-stats-grid">
                  <div className="quick-stat">
                    <div className="quick-stat-number">{dashboardStats.todayFeedback}</div>
                    <div className="quick-stat-label">Today's Feedback</div>
              </div>
                  <div className="quick-stat">
                    <div className="quick-stat-number">{dashboardStats.responseRate}%</div>
                    <div className="quick-stat-label">Response Rate</div>
            </div>
                  <div className="quick-stat">
                    <div className="quick-stat-number">{dashboardStats.totalUsers}</div>
                    <div className="quick-stat-label">Total Users</div>
          </div>
                  <div className="quick-stat">
                    <div className="quick-stat-number">{Array.isArray(recentFeedback) ? recentFeedback.filter(f => f.action_type === 'submitted').length : 0}</div>
                    <div className="quick-stat-label">Phone Numbers</div>
                </div>
                  </div>
                    </div>
                </div>

            {/* Recent Activity */}
            <div className="recent-activity-card">
              <div className="card-header">
                <h3>🕒 Recent Activity</h3>
                <button 
                  className="view-all-btn"
                  onClick={() => setActiveTab('feedback')}
                >
                  View All
                </button>
              </div>
              <div className="activity-list">
                {Array.isArray(recentFeedback) ? recentFeedback.slice(0, 5).map((feedback) => (
                  <div key={feedback.id} className="activity-item">
                    <div className="activity-icon">
                      {getEmojiForRating(feedback.rating)}
          </div>
                    <div className="activity-content">
                      <div className="activity-title">
                        {getRatingLabel(feedback.rating)} feedback from {feedback.location || 'Unknown'}
        </div>
                      <div className="activity-meta">
                        {new Date(feedback.created_at).toLocaleString()} • 
                        {feedback.phone_number && (feedback.rating === 'dissatisfied' || feedback.rating === 'very-dissatisfied') ? (
                          <span> Phone: <a href={`tel:${feedback.phone_number}`} className="phone-link" title="Click to call">{formatPhoneNumber(feedback.phone_number)}</a></span>
                        ) : (
                          <span>
                            {feedback.rating === 'very-satisfied' || feedback.rating === 'satisfied' || feedback.rating === 'neutral' 
                              ? ' No follow-up needed' 
                              : ' No phone'
                            }
                          </span>
                        )} • 
                        <span className={`action-badge ${feedback.action_type}`}>
                          {feedback.action_type === 'submitted' ? '✅ Submitted' :
                           feedback.action_type === 'skipped' ? '⏭️ Skipped' :
                           feedback.action_type === 'auto-timeout' ? '⏰ Auto-timeout' :
                           feedback.action_type}
                        </span>
                      </div>
                </div>
              </div>
            )) : []}
                {Array.isArray(recentFeedback) && recentFeedback.length === 0 && (
                  <div className="no-activity">
                    <p>📝 No recent activity</p>
                    <small>Customer feedback will appear here as it's submitted</small>
          </div>
                )}
        </div>
            </div>
          </motion.div>
        )}


      {activeTab === 'users' && (
        <motion.div 
          className="users-section"
          variants={tabVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="section-header">
            <h2>Location Users</h2>
            <button 
              className="create-btn"
              onClick={() => setShowCreateUser(!showCreateUser)}
            >
              + Create User
            </button>
          </div>

          {showCreateUser && (
            <motion.div 
              className="create-user-form"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <form onSubmit={handleCreateUser}>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Username"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Location Name"
                    value={newUser.location}
                    onChange={(e) => setNewUser({...newUser, location: e.target.value})}
                    required
                  />
                  <input
                    type="url"
                    placeholder="Review URL (optional)"
                    value={newUser.review_url}
                    onChange={(e) => setNewUser({...newUser, review_url: e.target.value})}
                  />
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  >
                    <option value="user">Location User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <select
                    value={newUser.session_expiry_days}
                    onChange={(e) => setNewUser({...newUser, session_expiry_days: parseInt(e.target.value)})}
                  >
                    <option value={5}>5 Days</option>
                    <option value={7}>7 Days (Default)</option>
                    <option value={10}>10 Days</option>
                    <option value={20}>20 Days</option>
                    <option value={-1}>Never Expire</option>
                  </select>
                </div>
                <div className="form-actions">
                  <button type="submit" className="submit-btn">Create User</button>
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={() => setShowCreateUser(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {loading ? (
            <div className="loading">Loading users...</div>
          ) : (
            <div className="users-grid">
              {Array.isArray(users) ? users.map((user) => (
                <motion.div 
                  key={user.id}
                  className="user-card"
                  whileHover={{ scale: 1.02 }}
                  layout
                >
                  <div className="user-info">
                    <h3>📍 {user.location}</h3>
                    <p><strong>Username:</strong> {user.username}</p>
                    <p><strong>Role:</strong> {user.role === 'admin' ? '👑 Admin' : '👤 Location User'}</p>
                    <p><strong>Review URL:</strong> {user.review_url || 'Default'}</p>
                    <p><strong>Session Expiry:</strong> {user.session_expiry_days === -1 ? '🔄 Never' : `⏰ ${user.session_expiry_days} days`}</p>
                    <p><strong>Created:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
                    <p><strong>User ID:</strong> {user.id}</p>
                  </div>
                  <div className="user-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => handleEditUser(user)}
                    >
                      ✏️ Edit
                    </button>
                    <button 
                      className="logout-btn"
                      onClick={() => handleLogoutUser(user.id, user.username)}
                    >
                      🚪 Logout
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </motion.div>
              )) : []}
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'analytics' && (
        <motion.div 
          className="analytics-section"
          variants={tabVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="analytics-header">
            <h2>📈 Analytics Dashboard</h2>
            <div className="analytics-summary">
              <div className="summary-item">
                <span className="summary-label">Total Feedback:</span>
                <span className="summary-value">{dashboardStats.totalFeedback}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Satisfaction Rate:</span>
                <span className="summary-value">{dashboardStats.satisfactionRate}%</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Response Rate:</span>
                <span className="summary-value">{dashboardStats.responseRate}%</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading analytics...</div>
          ) : (
            <div className="analytics-content">
              {/* Satisfaction Distribution Chart */}
              <div className="analytics-card">
                <div className="card-header">
                  <h3>😊 Satisfaction Distribution</h3>
                  <div className="chart-legend">
                    <span className="legend-item positive">Positive</span>
                    <span className="legend-item neutral">Neutral</span>
                    <span className="legend-item negative">Negative</span>
                  </div>
              </div>
                <div className="satisfaction-chart">
                  {Array.isArray(emojiAnalytics.emojis) ? emojiAnalytics.emojis.map((emojiData) => {
                    const percentage = dashboardStats.totalFeedback > 0 ? 
                      Math.round((emojiData.total / dashboardStats.totalFeedback) * 100) : 0;
                    const isPositive = emojiData.rating === 'very-satisfied' || emojiData.rating === 'satisfied';
                    const isNegative = emojiData.rating === 'dissatisfied' || emojiData.rating === 'very-dissatisfied';
                    
                    return (
                      <div key={emojiData.rating} className="chart-bar">
                        <div className="bar-info">
                          <span className="bar-emoji">{getEmojiForRating(emojiData.rating)}</span>
                          <span className="bar-label">{getRatingLabel(emojiData.rating)}</span>
                          <span className="bar-count">{emojiData.total}</span>
                    </div>
                        <div className="bar-container">
                          <div 
                            className={`bar-fill ${isPositive ? 'positive' : isNegative ? 'negative' : 'neutral'}`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                          <span className="bar-percentage">{percentage}%</span>
                        </div>
                    </div>
                    );
                  }) : []}
                  </div>
              </div>

              {/* Location Performance */}
              <div className="analytics-card">
                <div className="card-header">
                  <h3>📍 Location Performance</h3>
                          </div>
                <div className="location-performance">
                  {Array.isArray(analytics) ? analytics.map((location) => {
                    // Safely get values with fallbacks
                    const total = location.total || 0;
                    const verySatisfied = location.very_satisfied || 0;
                    const satisfied = location.satisfied || 0;
                    const dissatisfied = location.dissatisfied || 0;
                    const veryDissatisfied = location.very_dissatisfied || 0;
                    
                    const satisfactionRate = total > 0 ? 
                      Math.round(((verySatisfied + satisfied) / total) * 100) : 0;
                    
                    return (
                      <div key={location.location} className="location-performance-item">
                        <div className="location-info">
                          <span className="location-name">📍 {location.location || 'Unknown Location'}</span>
                          <span className="location-total">{total} total feedback</span>
                        </div>
                        <div className="location-metrics">
                          <div className="metric">
                            <span className="metric-label">Satisfaction:</span>
                            <span className={`metric-value ${satisfactionRate >= 70 ? 'good' : satisfactionRate >= 50 ? 'average' : 'poor'}`}>
                              {satisfactionRate}%
                            </span>
                          </div>
                          <div className="metric">
                            <span className="metric-label">Positive:</span>
                            <span className="metric-value">{verySatisfied + satisfied}</span>
                          </div>
                          <div className="metric">
                            <span className="metric-label">Negative:</span>
                            <span className="metric-value">{dissatisfied + veryDissatisfied}</span>
                          </div>
                        </div>
                        <div className="location-chart">
                          <div className="mini-chart">
                            <div 
                              className="chart-segment positive" 
                              style={{ width: `${total > 0 ? (verySatisfied / total) * 100 : 0}%` }}
                              title={`Very Satisfied: ${verySatisfied}`}
                            ></div>
                            <div 
                              className="chart-segment positive" 
                              style={{ width: `${total > 0 ? (satisfied / total) * 100 : 0}%` }}
                              title={`Satisfied: ${satisfied}`}
                            ></div>
                            <div 
                              className="chart-segment neutral" 
                              style={{ width: `${total > 0 ? ((location.neutral || 0) / total) * 100 : 0}%` }}
                              title={`Neutral: ${location.neutral || 0}`}
                            ></div>
                            <div 
                              className="chart-segment negative" 
                              style={{ width: `${total > 0 ? (dissatisfied / total) * 100 : 0}%` }}
                              title={`Dissatisfied: ${dissatisfied}`}
                            ></div>
                            <div 
                              className="chart-segment negative" 
                              style={{ width: `${total > 0 ? (veryDissatisfied / total) * 100 : 0}%` }}
                              title={`Very Dissatisfied: ${veryDissatisfied}`}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  }) : []}
                </div>
              </div>

              {/* QR Code Analytics */}
              <div className="analytics-card">
                <div className="card-header">
                  <h3>📱 QR Code Engagement</h3>
                </div>
                <div className="qr-analytics">
                  {Array.isArray(qrScanAnalytics.scans) && qrScanAnalytics.scans.length > 0 ? (
                    <div className="qr-stats-grid">
                      {Array.isArray(qrScanAnalytics.scans) ? qrScanAnalytics.scans.map((scanData) => {
                        const totalForRating = Array.isArray(emojiAnalytics.emojis) ? emojiAnalytics.emojis.find(e => e.rating === scanData.rating)?.total || 0 : 0;
                        const scanRate = totalForRating > 0 ? Math.round((scanData.total / totalForRating) * 100) : 0;
                        
                        return (
                          <div key={scanData.rating} className="qr-stat-card">
                            <div className="qr-stat-header">
                              <span className="qr-stat-emoji">{getEmojiForRating(scanData.rating)}</span>
                              <span className="qr-stat-label">{getRatingLabel(scanData.rating)}</span>
                          </div>
                            <div className="qr-stat-metrics">
                              <div className="qr-stat-number">{scanData.total}</div>
                              <div className="qr-stat-subtitle">QR Scans</div>
                              <div className="qr-stat-rate">{scanRate}% scan rate</div>
                            </div>
                            <div className="qr-stat-locations">
                            {Array.isArray(scanData.locations) ? scanData.locations.map((location) => (
                                <div key={location.location} className="qr-location-stat">
                                <span className="location-name">📍 {location.location}</span>
                                <span className="location-count">{location.count}</span>
                              </div>
                            )) : []}
                          </div>
                        </div>
                        );
                      }) : []}
                    </div>
                  ) : (
                    <div className="no-qr-data">
                      <div className="no-data-icon">📱</div>
                      <h4>No QR Code Scans Yet</h4>
                      <p>QR scans are tracked when "Very Satisfied" users scan the review QR code</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'feedback' && (
        <motion.div 
          className="feedback-section"
          variants={tabVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="feedback-header-section">
            <h2>📝 Customer Feedback</h2>
            <div className="feedback-summary-cards">
              {(() => {
                const stats = getFilteredSummaryStats();
                return (
                  <>
                    <div className="summary-card total">
                      <div className="summary-number">{stats.total}</div>
                      <div className="summary-label">Total Feedback</div>
                    </div>
                    <div className="summary-card submitted">
                      <div className="summary-number">{stats.withPhone}</div>
                      <div className="summary-label">With Phone Numbers</div>
                    </div>
                    <div className="summary-card positive">
                      <div className="summary-number">{stats.positive}</div>
                      <div className="summary-label">Positive</div>
                    </div>
                    <div className="summary-card negative">
                      <div className="summary-number">{stats.negative}</div>
                      <div className="summary-label">Negative</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading feedback...</div>
          ) : (
            <div className="feedback-content">
              {Array.isArray(recentFeedback) && recentFeedback.length > 0 ? (
                <div className="feedback-filters">
                  <div className="filter-group">
                    <label>Filter by Rating:</label>
                    <select onChange={(e) => setFeedbackFilter(e.target.value)}>
                      <option value="all">All Ratings</option>
                      <option value="very-satisfied">😊 Very Satisfied</option>
                      <option value="satisfied">😊 Satisfied</option>
                      <option value="neutral">🤔 Neutral</option>
                      <option value="dissatisfied">😢 Dissatisfied</option>
                      <option value="very-dissatisfied">😠 Very Dissatisfied</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Filter by Action:</label>
                    <select onChange={(e) => setActionFilter(e.target.value)}>
                      <option value="all">All Actions</option>
                      <option value="submitted">✅ Submitted</option>
                      <option value="skipped">⏭️ Skipped</option>
                      <option value="auto-timeout">⏰ Auto-timeout</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Time Period:</label>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="thisWeek">This Week</option>
                      <option value="lastWeek">Last Week</option>
                      <option value="thisMonth">This Month</option>
                      <option value="lastMonth">Last Month</option>
                      <option value="rating">By Rating</option>
                      <option value="location">By Location</option>
                      <option value="custom">Custom (All Time)</option>
                    </select>
                  </div>
                </div>
              ) : null}

              <div className="feedback-list">
                {Array.isArray(filteredFeedback) && filteredFeedback.length > 0 ? (
                  <div className="feedback-cards">
                    {Array.isArray(filteredFeedback) ? filteredFeedback.map((feedback) => (
                      <div key={feedback.id} className="feedback-card">
                        <div className="feedback-card-header">
                          <div className="feedback-rating">
                            <span className="feedback-emoji">{getEmojiForRating(feedback.rating)}</span>
                            <span className="feedback-rating-label">{getRatingLabel(feedback.rating)}</span>
                          </div>
                          <div className="feedback-time">
                            {new Date(feedback.created_at).toLocaleString()}
                          </div>
                        </div>
                        
                        <div className="feedback-card-body">
                          <div className="feedback-location">
                            <span className="location-icon">📍</span>
                            <span className="location-name">{feedback.location || 'Unknown Location'}</span>
                          </div>
                          
                          <div className="feedback-details">
                            <div className="feedback-phone">
                              <span className="phone-icon">📞</span>
                              {feedback.phone_number && (feedback.rating === 'dissatisfied' || feedback.rating === 'very-dissatisfied') ? (
                                <a 
                                  href={`tel:${feedback.phone_number}`}
                                  className="phone-number clickable"
                                  title="Click to call"
                                >
                                  {formatPhoneNumber(feedback.phone_number)}
                                </a>
                              ) : (
                                <span className="no-phone">
                                  {feedback.rating === 'very-satisfied' || feedback.rating === 'satisfied' || feedback.rating === 'neutral' 
                                    ? 'No follow-up needed' 
                                    : 'No phone number provided'
                                  }
                                </span>
                              )}
                            </div>
                            
                            <div className="feedback-action">
                              <span className={`action-badge ${feedback.action_type}`}>
                                {feedback.action_type === 'submitted' ? '✅ Submitted' :
                                 feedback.action_type === 'skipped' ? '⏭️ Skipped' :
                                 feedback.action_type === 'auto-timeout' ? '⏰ Auto-timeout' :
                                 feedback.action_type}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="feedback-card-footer">
                          <div className="feedback-id">ID: {feedback.id}</div>
                          {feedback.action_type === 'submitted' && (feedback.rating === 'dissatisfied' || feedback.rating === 'very-dissatisfied') && (
                            <div className="follow-up-required">
                              <span className="follow-up-icon">🚨</span>
                              <span className="follow-up-text">Follow-up Required</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )) : []}
                  </div>
                ) : (
                  <div className="no-feedback">
                    <div className="no-feedback-icon">📝</div>
                    <h3>{Array.isArray(recentFeedback) && recentFeedback.length === 0 ? 'No feedback recorded yet' : 'No feedback matches your filters'}</h3>
                    <p>{Array.isArray(recentFeedback) && recentFeedback.length === 0 ? 'Customer feedback will appear here as it\'s submitted' : 'Try adjusting your filter criteria'}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay">
          <motion.div 
            className="modal-content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3>Edit User</h3>
            <form onSubmit={handleUpdateUser}>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Username"
                  value={editingUser.username}
                  onChange={(e) => setEditingUser({...editingUser, username: e.target.value})}
                  required
                />
                <input
                  type="password"
                  placeholder="New Password (leave empty to keep current)"
                  value={editingUser.password}
                  onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                />
                <input
                  type="text"
                  placeholder="Location Name"
                  value={editingUser.location}
                  onChange={(e) => setEditingUser({...editingUser, location: e.target.value})}
                  required
                />
                <input
                  type="url"
                  placeholder="Review URL (optional)"
                  value={editingUser.review_url || ''}
                  onChange={(e) => setEditingUser({...editingUser, review_url: e.target.value})}
                />
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                >
                  <option value="user">Location User</option>
                  <option value="admin">Admin</option>
                </select>
                <select
                  value={editingUser.session_expiry_days || 7}
                  onChange={(e) => setEditingUser({...editingUser, session_expiry_days: parseInt(e.target.value)})}
                >
                  <option value={5}>5 Days</option>
                  <option value={7}>7 Days (Default)</option>
                  <option value={10}>10 Days</option>
                  <option value={20}>20 Days</option>
                  <option value={-1}>Never Expire</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn">Update User</button>
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setEditingUser(null)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="modal-overlay">
          <motion.div 
            className="modal-content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3>Change Admin Password</h3>
            <form onSubmit={handleChangePassword}>
              <div className="form-row">
                <input
                  type="password"
                  placeholder="Current Password"
                  value={passwordChange.currentPassword}
                  onChange={(e) => setPasswordChange({...passwordChange, currentPassword: e.target.value})}
                  required
                />
                <input
                  type="password"
                  placeholder="New Password"
                  value={passwordChange.newPassword}
                  onChange={(e) => setPasswordChange({...passwordChange, newPassword: e.target.value})}
                  required
                />
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={passwordChange.confirmPassword}
                  onChange={(e) => setPasswordChange({...passwordChange, confirmPassword: e.target.value})}
                  required
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn">Change Password</button>
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setShowChangePassword(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      </div> {/* Close admin-content */}
    </motion.div>
  );
};

export default AdminDashboard;
